import type { MailboxInfo } from 'owa-client-types';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { isLicensingMailboxInfo } from 'owa-account-source-list-types/lib/utils/isLicensingMailboxInfo';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { AccountSourceType } from 'owa-account-source-list-types';
import {
    getCoprincipalAccountForMailboxInfo,
    getGlobalSettingsAccountMailboxInfo,
} from 'owa-account-source-list-store';

/**
 * Resolves a mailbox info to its linked licensing mailbox info if available.
 *
 * This utility can be safely called at any level of the stack (UI components, service layers, etc.)
 * and will correctly resolve to the licensing mailbox. If already a licensing mailbox or no linked
 * licensing mailbox exists, returns the original mailbox info.
 *
 * @param mailboxInfo - The mailbox info to resolve
 * @returns The linked licensing mailbox info if available, otherwise the original mailbox info
 */
export function getLinkedLicensingMailboxInfoOrSelf(mailboxInfo: MailboxInfo): MailboxInfo {
    // If UIB mailboxInfo is passed, use global settings account instead
    const effectiveMailboxInfo = isUnifiedInboxMailboxInfo(mailboxInfo)
        ? getGlobalSettingsAccountMailboxInfo()
        : mailboxInfo;

    if (!isCloudCacheBizchatEnabled(effectiveMailboxInfo)) {
        return effectiveMailboxInfo;
    }

    // Already a licensing mailbox, return as-is
    if (isLicensingMailboxInfo(effectiveMailboxInfo)) {
        return effectiveMailboxInfo;
    }

    // Check for linked licensing mailbox in coprincipal account
    const account = getCoprincipalAccountForMailboxInfo(effectiveMailboxInfo);
    if (account?.licensingMailboxInfo) {
        return account.licensingMailboxInfo;
    }

    return effectiveMailboxInfo;
}

export function isCloudCacheBizchatEnabled(mailboxInfo: MailboxInfo): boolean {
    if (!isGlobalFeatureEnabled('acct-enable-cloud-cache-bizchat')) {
        return false;
    }

    const account = getCoprincipalAccountForMailboxInfo(mailboxInfo);
    return account?.sourceType === AccountSourceType.Google;
}
